import { Add, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { Box, Button, Card, IconButton, Tooltip, Typography } from "@mui/material";
import { useModals } from "../../../../context/ModalContext";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { roundMoney } from "../../../scope-of-work/components/ListTable/components/calculateTotal";
import { ResettableInput } from "../../../scope-of-work/components/ResettableInput";
import { invoiceEditorInnerBorder, invoiceEditorSectionCardSx } from "../../../ui/invoiceEditorSectionSurface";
import { DiscountModalForm } from "./DiscountModalForm";
import type { InvoiceDiscountApplyToOption, InvoiceDiscountDraft } from "./InvoiceDiscountsCard";
import type { InvoiceServiceFeeDraft } from "./InvoiceServiceFeesCard";
import { ServiceFeeModalForm } from "./ServiceFeeModalForm";

type Props = {
  lineItemsSubtotal: number | null;
  serviceFeeAmount: number;
  serviceFeeLabel?: string;
  extraLineItemsAmount: number;
  discountsAmount?: number;
  /** Subtotal + fee before any manual total override. */
  computedGrandTotal: number;
  /** Amount shown as Total (may equal computed or user override). */
  displayTotal: number;
  isTotalOverridden: boolean;
  onTotalChange: (value: number | null) => void;
  onTotalReset: () => void;
  /** Optional interactive service fees management. */
  serviceFees?: InvoiceServiceFeeDraft[];
  serviceFeeApplyToOptions?: InvoiceDiscountApplyToOption[];
  serviceFeeComputedAmounts?: Record<string, number>;
  onAddServiceFee?: (draft?: Omit<InvoiceServiceFeeDraft, "id">) => void;
  onRemoveServiceFee?: (id: string) => void;
  onChangeServiceFee?: (id: string, patch: Partial<InvoiceServiceFeeDraft>) => void;
  showServiceFeeValidation?: boolean;
  discounts?: InvoiceDiscountDraft[];
  discountApplyToOptions?: InvoiceDiscountApplyToOption[];
  discountComputedAmounts?: Record<string, number>;
  onAddDiscount?: (draft?: Omit<InvoiceDiscountDraft, "id">) => void;
  onRemoveDiscount?: (id: string) => void;
  onChangeDiscount?: (id: string, patch: Partial<InvoiceDiscountDraft>) => void;
  showDiscountValidation?: boolean;
  /** When embedded, sits at the bottom of Line Items card without its own Card frame. */
  variant?: "card" | "embedded";
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export function InvoiceMultiJobTotalsCard({
  lineItemsSubtotal,
  serviceFeeAmount,
  serviceFeeLabel = "Service fee",
  extraLineItemsAmount,
  discountsAmount = 0,
  computedGrandTotal,
  displayTotal,
  isTotalOverridden,
  onTotalChange,
  onTotalReset,
  serviceFees,
  serviceFeeApplyToOptions,
  serviceFeeComputedAmounts,
  onAddServiceFee,
  onRemoveServiceFee,
  onChangeServiceFee,
  discounts,
  discountApplyToOptions,
  discountComputedAmounts,
  onAddDiscount,
  onRemoveDiscount,
  onChangeDiscount,
  variant = "card",
}: Props) {
  const { closeByKey } = useModals();
  const subLabel = lineItemsSubtotal != null && lineItemsSubtotal > 0 ? formatCurrency(lineItemsSubtotal) : "Enter a service amount for each job";

  const content = (
    <Box
      sx={{
        p: 3,
        bgcolor: "grey.50",
        borderTop: variant === "embedded" ? invoiceEditorInnerBorder : undefined,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 440, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Subtotal row */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
            Subtotal (service items)
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
            {subLabel}
          </Typography>
        </Box>

        {/* Extra line items row */}
        {extraLineItemsAmount > 0 ? (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Extra line items
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
              +{formatCurrency(extraLineItemsAmount)}
            </Typography>
          </Box>
        ) : null}

        {/* Discounts rows*/}
        {discounts && discounts.length > 0 ? (
          discounts.map((discount) => {
            const discountAmount = discountComputedAmounts?.[discount.id] ?? 0;
            const applyOption = discountApplyToOptions?.find((opt) => opt.key === (discount.appliesToKey || "invoice"));
            const applyLabel = applyOption && applyOption.key !== "invoice" ? applyOption.label : null;
            const editModalKey = `edit-multijob-discount-${discount.id}`;

            return (
              <Box key={discount.id} display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                    {discount.description || "Discount"}
                    {discount.kind === "percentage" ? ` (${discount.value}%)` : ""}
                    {applyLabel ? ` — ${applyLabel}` : ""}
                  </Typography>
                  <BaseModalComponent
                    modalKey={editModalKey}
                    modalLabel="Edit discount"
                    defaultSizes={{
                      width: 500,
                      height: 400,
                    }}
                    preventParentTrigger
                    allowInsidePreventModal
                    elementTrigger={
                      <Tooltip title="Edit discount">
                        <IconButton size="small" sx={{ p: 0.25, color: "action.active" }}>
                          <EditOutlined sx={{ fontSize: "0.95rem" }} />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <DiscountModalForm
                      initialValues={discount}
                      applyToOptions={discountApplyToOptions}
                      onSave={(data) => {
                        onChangeDiscount?.(discount.id, data);
                        closeByKey(editModalKey);
                      }}
                      onCancel={() => closeByKey(editModalKey)}
                      isEdit
                    />
                  </BaseModalComponent>
                  <Tooltip title="Remove discount">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveDiscount?.(discount.id)}
                      sx={{ p: 0.25, color: "action.active", "&:hover": { color: "error.main" } }}
                    >
                      <DeleteOutline sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                  -{formatCurrency(discountAmount)}
                </Typography>
              </Box>
            );
          })
        ) : discountsAmount > 0 ? (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Discounts
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
              -{formatCurrency(discountsAmount)}
            </Typography>
          </Box>
        ) : null}

        {/* Service Fees clean rows with Edit / Remove */}
        {serviceFees && serviceFees.length > 0 ? (
          serviceFees.map((fee) => {
            const feeAmount = serviceFeeComputedAmounts?.[fee.id] ?? 0;
            const applyOption = serviceFeeApplyToOptions?.find((opt) => opt.key === (fee.appliesToKey || "invoice"));
            const applyLabel = applyOption && applyOption.key !== "invoice" ? applyOption.label : null;
            const editModalKey = `edit-service-fee-${fee.id}`;

            return (
              <Box key={fee.id} display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                    {fee.label || "Service fee"}
                    {fee.kind === "percentage" ? ` (${fee.value}%)` : ""}
                    {applyLabel ? ` — ${applyLabel}` : ""}
                  </Typography>
                  <BaseModalComponent
                    modalKey={editModalKey}
                    modalLabel="Edit service fee"
                    defaultSizes={{
                      width: 500,
                      height: 430,
                    }}
                    preventParentTrigger
                    allowInsidePreventModal
                    elementTrigger={
                      <Tooltip title="Edit fee">
                        <IconButton size="small" sx={{ p: 0.25, color: "action.active" }}>
                          <EditOutlined sx={{ fontSize: "0.95rem" }} />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ServiceFeeModalForm
                      initialValues={fee}
                      applyToOptions={serviceFeeApplyToOptions}
                      onSave={(data) => {
                        onChangeServiceFee?.(fee.id, data);
                        closeByKey(editModalKey);
                      }}
                      onCancel={() => closeByKey(editModalKey)}
                      isEdit
                    />
                  </BaseModalComponent>
                  <Tooltip title="Remove fee">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveServiceFee?.(fee.id)}
                      sx={{ p: 0.25, color: "action.active", "&:hover": { color: "error.main" } }}
                    >
                      <DeleteOutline sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                  +{formatCurrency(feeAmount)}
                </Typography>
              </Box>
            );
          })
        ) : serviceFeeAmount > 0 ? (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              {serviceFeeLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
              +{formatCurrency(serviceFeeAmount)}
            </Typography>
          </Box>
        ) : null}

        {/* Action Buttons: Add Discount & Add Service Fee */}
        {(onAddDiscount || onAddServiceFee) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, px: 0.5, mt: -0.5 }}>
            {onAddDiscount && (
              <BaseModalComponent
                modalKey="add-multijob-invoice-discount"
                modalLabel="Add discount"
                defaultSizes={{
                  width: 500,
                  height: 400,
                }}
                preventParentTrigger
                allowInsidePreventModal
                elementTrigger={
                  <Button
                    size="small"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", fontSize: "0.8125rem", fontWeight: 700, p: 0, minWidth: 0 }}
                  >
                    Add discount
                  </Button>
                }
              >
                <DiscountModalForm
                  applyToOptions={discountApplyToOptions}
                  onSave={(data) => {
                    onAddDiscount?.(data);
                    closeByKey("add-multijob-invoice-discount");
                  }}
                  onCancel={() => closeByKey("add-multijob-invoice-discount")}
                />
              </BaseModalComponent>
            )}

            {onAddServiceFee && (
              <BaseModalComponent
                modalKey="add-invoice-service-fee"
                modalLabel="Add service fee"
                defaultSizes={{
                  width: 500,
                  height: 430,
                }}
                preventParentTrigger
                allowInsidePreventModal
                elementTrigger={
                  <Button
                    size="small"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", fontSize: "0.8125rem", fontWeight: 700, p: 0, minWidth: 0 }}
                  >
                    Add service fee
                  </Button>
                }
              >
                <ServiceFeeModalForm
                  applyToOptions={serviceFeeApplyToOptions}
                  onSave={(data) => {
                    onAddServiceFee?.(data);
                    closeByKey("add-invoice-service-fee");
                  }}
                  onCancel={() => closeByKey("add-invoice-service-fee")}
                />
              </BaseModalComponent>
            )}
          </Box>
        )}

        {/* Grand Total row */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{
            pt: 2,
            mt: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            px: 0.5,
          }}
        >
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
              Total
            </Typography>
            {isTotalOverridden && (
              <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary", display: "block" }}>
                Adjusted
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25, minWidth: 140 }}>
            <ResettableInput
              defaultValue={roundMoney(computedGrandTotal)}
              value={roundMoney(displayTotal)}
              onChange={onTotalChange}
              onReset={onTotalReset}
              readOnly={false}
              formatValue={formatCurrency}
              width="140px"
              fontSize="1.25rem"
              fontWeight={700}
              align="right"
              showResetButton
              resetTooltip="Restore total from line items + service fee"
              emptyCommitValue={roundMoney(computedGrandTotal)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  if (variant === "embedded") {
    return content;
  }

  return (
    <Card elevation={0} sx={invoiceEditorSectionCardSx}>
      <Box sx={{ p: 2, borderBottom: invoiceEditorInnerBorder }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
          Totals
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", mt: 0.5 }}>
          Total is the sum of job line amounts, extra line items, discounts, then service fees. You can override it and use Reset to restore the
          calculated value.
        </Typography>
      </Box>
      {content}
    </Card>
  );
}
